<template>
  <div
    v-if="showUpsell && !upsellClicked"
    :class="bem({ upsellClicked })"
  >
    <div :class="[bem('image-container'), {'vision-shimmer': !imageLoaded}]">
      <DynamicImg
        :view-angle="2"
        :sku="previewProductSku"
        :width="100"
        :height="100"
        bg="transparent"
        :class="bem('image')"
        @loaded="handleImageLoad"
      />
    </div>
    <div :class="bem('content')">
      <VisionHeading
        tag="h3"
        size="h5"
        :class="bem('title')"
      >
        <template v-if="priceSaving">
          Save {{ $tca(priceSaving) }} on an additional Lens
        </template>
        <!-- eslint-disable vue/no-v-html -->
        <span v-else v-html="upsell.product.lensChoice.name" />
        <!-- eslint-enable vue/no-v-html -->
      </VisionHeading>

      <ChoiceSelect
        :customised-product="upsell.product"
        :choices="options.slice(0, 6)"
        :part="upsell.part"
        shape="circle"
        display="flex"
        wrap
        size="small"
        :class="bem('swatch-select')"
        background-style="transparent"
        @select="({ sku }) => selectedOptionChanged(sku)"
      />
    </div>
    <div :class="bem('cta-container')">
      <VisionHeading
        v-if="priceSaving"
        tag="h3"
        size="h5"
        :class="bem('original-price')"
      >
        <s>
          {{ $tca(upsell.product.price) }}
        </s>
      </VisionHeading>
      <VisionLink
        :line-link="true"
        theme="gradient"
        :is-button="true"
        :class="bem('cta')"
        @click="addToCart"
      >
        Add for {{ $tca(calculatedPrice) }}
      </VisionLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import CustomisedProduct from '~/models/CustomisedProduct'
import { buildCartItems } from '~/assets/js/helpers/shopify'
const { $track, $sentry, $upsell } = useNuxtApp()

const bem = useBEM('CartItemLensKitUpsell')
const shopifyStore = useShopifyStore();

const props = defineProps({
  displayOrder: {
    type: Number,
    required: false,
    default: undefined,
  },
  customisedProduct: {
    type: CustomisedProduct,
    default: () => ({}),
  },
})

const upsells = ref<any>(null)
const upsellClicked = ref<boolean>(false)
const imageLoaded = ref<boolean>(false)

upsells.value = await $upsell.getUpsellsForProduct(
  props.customisedProduct,
)

const upsell = computed(() => {
  return upsells.value?.find((upsell: any) => upsell.mode === 'lens_kit')
})


const lensSku = computed(() => {
  return upsell.value?.product?.lensChoice.sku
})

const previewProductSku = computed(() => {
  const currentLensSku = props.customisedProduct.lensChoice.sku
  return props.customisedProduct.legacySKU.replace(currentLensSku, lensSku.value)
})

const currentVLTCategory = computed(() => {
  const lensChoice = props.customisedProduct.lensChoice
  return getVLTCategory(lensChoice)
})

const alreadyInCart = computed(() => {
  const cartItemSkus = Object.values(shopifyStore.bundledCartItems)
    .map((item: any) => item.summary.bundleSku)
    .flatMap((bundleSku: string) => bundleSku.split('-'));
  const optionSkus = options.value.map((option: any) => option.sku);
  return optionSkus.some((sku: string) => cartItemSkus.includes(sku));
});

const options = computed(() => {
  return upsell.value?.availableOptions
})

const showUpsell = computed(() => {
  if (!upsell.value?.product) return false
  if (!currentVLTCategory.value) return false
  if (!options?.value?.length) return false
  if (alreadyInCart.value) return false
  return true
})

if (options.value?.length) selectedOptionChanged(options.value[0]?.sku)

const availableDiscount = computed(() => {
  return shopifyStore.getDiscountsForProductFromCartItems(
    upsell.value?.product,
    { sku: upsell.value.upsellProduct.sku }
  )?.[0]
})

const calculatedPrice = computed(() => {
  if (availableDiscount.value) {
    if (availableDiscount.value.discountSummary.discountType === 'DiscountPercentage') {
      return upsell.value.product.price * (1 - availableDiscount.value.discountSummary.discountPercentageOff)
    }

    if (availableDiscount.value.discountSummary.discountType === 'DiscountAmount') {
      return upsell.value.product.price - availableDiscount.value.discountSummary.discountAmountOff
    }
  }

  return upsell.value.product.price
})

const priceSaving = computed(() => {
  if (!upsell.value?.product?.price) return 0

  return upsell.value.product.price - calculatedPrice.value
})

watch(options, () => {
  if (!upsell.value?.product) return;

  const currentChoice = upsell.value.product.lensChoice
  const isCurrentChoiceValid = options.value.some(
    (option: any) => option.sku === currentChoice?.sku,
  )
  if (!currentChoice || !isCurrentChoiceValid) {
    selectedOptionChanged(options.value[0]?.sku)
  }
});

watch(lensSku, () => {
  imageLoaded.value = false
})

function selectedOptionChanged(sku) {
  if (!sku) return
  upsell.value.product.setChoice(sku)
}

async function addToCart() {
  upsellClicked.value = true

  const attributes = [
    {
      key: '_isUpsellItem',
      value: 'true',
    },
  ]

  if (props.displayOrder)
    attributes.push({
      key: '_bundleDisplayOrder',
      value: String(props.displayOrder + 0.5),
    })

  try {
    const newCartItems: any = buildCartItems(upsell.value.product, attributes)

    await shopifyStore.addCartItems(newCartItems)

    $track.product.add(upsell.value.product, {
      location: 'inline-lens-kit-upsell',
      quantity: 1,
    })
  } catch (error) {
    $sentry.captureException(error)
    console.log(error)
  }

  upsellClicked.value = false
}

function handleImageLoad() {
  imageLoaded.value = true
}

function getVLTCategory(lensOption: any) {
  const vlt = lensOption?.vlt
  if (!vlt) return null
  if (vlt?.includes('-')) return null // Let's not mess around with Iris
  return parseInt(vlt) > 30 ? 'high' : 'low'
}
</script>

<style lang="postcss">
.CartItemLensKitUpsell {
  @apply mt-4 grid items-center gap-3;
  grid-template-columns: auto 1fr auto;

  &--cart-updating {
    @apply opacity-50 cursor-not-allowed;
  }
}

.CartItemLensKitUpsell__description {
  @apply text-xxs font-light;
}

.CartItemLensKitUpsell__swatch-select {
  @apply mt-2;
}

.CartItemLensKitUpsell__image-container {
  @apply w-16;

  &.vision-shimmer .CartItemLensKitUpsell__image {
    @apply opacity-0;
  }
}

.CartItemLensKitUpsell__cta {
  @apply text-xs ml-0.5 whitespace-nowrap;
}

.CartItemLensKitUpsell__cta-container {
  @apply flex flex-col items-end;
}
</style>
